/**
 * syncSummary.ts — turns the sync engine's counters into one honest status.
 *
 * Only covers a device that is signed in and syncing; offline, stopped and
 * lapsed are described by their callers. The states:
 *   • synced:    nothing waiting.
 *   • sending:   changes are going through (one went through recently).
 *   • slow:      going through, but slowly enough that something may be wrong.
 *   • stalled:   changes are waiting and none has gone through for a minute,
 *                or the server cannot be reached.
 *   • pages:     only closed pages hold unsent edits.
 *   • attention: the server refused some changes; the user must decide.
 */

export type SyncHealth = 'synced' | 'sending' | 'slow' | 'stalled' | 'pages' | 'attention'

export interface SyncSummaryInput {
  now: number
  /** Outbox items waiting to be sent automatically. */
  waiting: number
  /** Outbox items the server refused, waiting for the user. */
  failed: number
  /** Closed pages holding unsent edits. */
  pages: number
  isSyncing: boolean
  serverUnreachable: boolean
  lastSentAt: number | null
  /** When the waiting count last rose from zero. */
  waitingSince: number | null
  /** Most waiting items seen since the queue was last empty. */
  backlogTotal: number
  /** Items sent per minute, measured over the last two minutes. */
  ratePerMinute: number
  nextRetryAt: number | null
}

export interface SyncSummary {
  health: SyncHealth
  label: string
  tooltip: string
  /** good: green; busy: amber and moving; warn: amber and still. */
  tone: 'good' | 'busy' | 'warn'
}

export const STALL_AFTER_MS = 60_000
/** A backlog expected to take longer than this is slow. */
export const SLOW_ETA_MS = 10 * 60_000
/** Fewer items per minute than this (over 10s each) is slow. */
export const SLOW_RATE_PER_MINUTE = 6
/** How long to measure before calling the rate slow. */
const RATE_WARMUP_MS = 30_000

const n = (value: number) => value.toLocaleString()
const plural = (count: number, one: string, many = `${one}s`) => `${n(count)} ${count === 1 ? one : many}`

export function formatWait(ms: number): string {
  const seconds = Math.max(1, Math.ceil(ms / 1000))
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.ceil(seconds / 60)
  if (minutes < 60) return `${minutes} min`
  return `${Math.round(minutes / 60)} h`
}

export function summarizeSync(input: SyncSummaryInput): SyncSummary {
  const { now, waiting, failed, pages } = input
  const attentionNote = failed > 0 ? ` ${plural(failed, 'change')} also need${failed === 1 ? 's' : ''} your attention.` : ''

  if (waiting > 0) {
    const retryIn = input.nextRetryAt && input.nextRetryAt > now ? formatWait(input.nextRetryAt - now) : null

    if (input.serverUnreachable) {
      return {
        health: 'stalled',
        label: retryIn ? `Stalled · retrying in ${retryIn}` : 'Stalled · retrying',
        tooltip: `Can’t reach the sync server. ${plural(waiting, 'change')} ${waiting === 1 ? 'is' : 'are'} saved on this device and will send automatically. Click for details.${attentionNote}`,
        tone: 'warn',
      }
    }

    const quietSince = Math.max(input.lastSentAt ?? 0, input.waitingSince ?? now)
    if (now - quietSince > STALL_AFTER_MS) {
      return {
        health: 'stalled',
        label: retryIn ? `Stalled · retrying in ${retryIn}` : 'Stalled',
        tooltip: `Nothing has synced for ${formatWait(now - quietSince)}. ${plural(waiting, 'change')} ${waiting === 1 ? 'is' : 'are'} saved on this device and waiting. Click for details.${attentionNote}`,
        tone: 'warn',
      }
    }

    const measuring = input.waitingSince !== null && now - input.waitingSince >= RATE_WARMUP_MS
    const etaMs = input.ratePerMinute > 0 ? (waiting / input.ratePerMinute) * 60_000 : null
    if (measuring && input.ratePerMinute > 0
      && (input.ratePerMinute < SLOW_RATE_PER_MINUTE || (etaMs !== null && etaMs > SLOW_ETA_MS))) {
      return {
        health: 'slow',
        label: `Slow · ${n(waiting)} left`,
        tooltip: `Syncing slowly: about ${n(Math.round(input.ratePerMinute))} per minute${etaMs ? `, roughly ${formatWait(etaMs)} to go` : ''}. Your changes are saved on this device.${attentionNote}`,
        tone: 'busy',
      }
    }

    const total = Math.max(input.backlogTotal, waiting)
    const sent = total - waiting
    const eta = etaMs !== null && etaMs >= 60_000 ? ` · ~${formatWait(etaMs)}` : ''
    return {
      health: 'sending',
      label: total > 1 ? `Sending ${n(sent)} of ${n(total)}${eta}` : 'Sending…',
      tooltip: `Sending ${plural(waiting, 'change')} to the cloud.${attentionNote}`,
      tone: 'busy',
    }
  }

  if (failed > 0) {
    return {
      health: 'attention',
      label: `${n(failed)} need${failed === 1 ? 's' : ''} attention`,
      tooltip: `The server refused ${plural(failed, 'change')}, so ${failed === 1 ? 'it' : 'they'} won’t be retried automatically. ${failed === 1 ? 'It is' : 'They are'} kept on this device. Click to see what and why.`,
      tone: 'warn',
    }
  }

  if (pages > 0) {
    return {
      health: 'pages',
      label: `${plural(pages, 'page')} not sent`,
      tooltip: `Edits to ${plural(pages, 'closed page')} haven’t reached the cloud yet. They are saved on this device. Click to see which.`,
      tone: 'warn',
    }
  }

  if (input.isSyncing) {
    return { health: 'sending', label: 'Syncing…', tooltip: 'Checking for changes.', tone: 'busy' }
  }

  return { health: 'synced', label: 'Synced', tooltip: 'Online — all changes synchronized', tone: 'good' }
}
